import React, { useMemo, useRef, useState, useLayoutEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Bar } from '@visx/shape';
import type { TimelineItem as TimelineItemType, HoverPosition } from '../../../types/timeline';
import { TIMELINE_COLORS } from '../../../constants/timeline';

interface TimelineItemProps {
  item: TimelineItemType;
  xScale: (date: Date) => number;
  yPosition: number;
  onItemClick: (item: TimelineItemType) => void;
  onItemHover: (item: TimelineItemType | null, position?: HoverPosition) => void;
  isHovered?: boolean;
  containerWidth: number;
}

const TimelineItem: React.FC<TimelineItemProps> = React.memo(({
  item,
  xScale,
  yPosition,
  onItemClick,
  onItemHover,
  isHovered = false,
  containerWidth
}) => {
  const [isMouseOver, setIsMouseOver] = useState(false);
  const labelRef = useRef<SVGTextElement>(null);
  const [labelWidth, setLabelWidth] = useState(0);
  
  // Calculate positions once and only recalculate when dependencies change
  const itemPositions = useMemo(() => {
    const startX = xScale(item.startDate);
    const endX = item.endDate ? xScale(item.endDate) : startX + 10;
    const width = Math.max(endX - startX, 10);
    
    // Determine if the item is in view
    const isOutOfBoundsLeft = startX < -100;
    const isOutOfBoundsRight = startX > containerWidth + 100;
    
    // Check if bar extends beyond the viewport
    const extendsLeft = startX < 0 && endX > 0;
    const extendsRight = startX < containerWidth && endX > containerWidth;
    
    // Always make work experience items visible
    const isVisible = item.type === 'workExperience' || (!isOutOfBoundsLeft && !isOutOfBoundsRight);
    
    // Calculate a position hash for forcing updates
    const positionHash = `${Math.round(startX)}-${Math.round(endX)}`;
    
    // Calculate visible portion of the item
    const visibleStartX = Math.max(0, startX);
    const visibleEndX = Math.min(containerWidth, endX);
    const visibleWidth = Math.max(0, visibleEndX - visibleStartX);
    
    return {
      startX,
      endX,
      width,
      visibleStartX,
      visibleEndX,
      visibleWidth,
      isVisible,
      isOutOfBoundsLeft,
      isOutOfBoundsRight,
      extendsLeft,
      extendsRight,
      positionHash
    };
  }, [item.startDate, item.endDate, item.type, xScale, containerWidth]);
  
  // Measure label width once after render
  useLayoutEffect(() => {
    if (labelRef.current && item.type === 'workExperience') {
      // Only measure once or when text changes
      const width = labelRef.current.getComputedTextLength();
      if (Math.abs(width - labelWidth) > 1) {
        setLabelWidth(width);
      }
    }
  }, [item.title, item.type, labelWidth]);
  
  // Calculate if label can fit inside the work experience bar
  const canLabelFitInside = useMemo(() => {
    return item.type === 'workExperience' && itemPositions.width > labelWidth + 20;
  }, [item.type, itemPositions.width, labelWidth]);
  
  // Calculate label position
  const labelPosition = useMemo(() => {
    if (item.type !== 'workExperience') return null;
    
    // Determine if item is fully visible in the viewport
    const isFullyVisible = itemPositions.startX >= 0 && itemPositions.endX <= containerWidth;
    
    // Calculate threshold for determining if label fits inside completely
    // Add padding to ensure the label doesn't get too close to the edges
    const LABEL_PADDING = 20; // Minimum distance from edge of bar
    const labelRequiredWidth = labelWidth + (LABEL_PADDING * 2);
    
    // Check if the visible portion of the bar is wide enough for the label
    const labelFitsCompletely = itemPositions.visibleWidth >= labelRequiredWidth;
    
    // Always prefer center positioning when the label fits completely within the bar
    if (labelFitsCompletely && isFullyVisible) {
      // For fully visible bars where label fits completely, center the label
      return {
        x: itemPositions.startX + itemPositions.width / 2,
        y: yPosition + 14,
        textAnchor: 'middle' as const,
        isInside: true
      };
    }
    
    // Special case: If bar extends beyond both left and right edges of viewport
    if (itemPositions.extendsLeft && itemPositions.extendsRight) {
      // Center the label in the viewport
      return {
        x: containerWidth / 2,
        y: yPosition + 14,
        textAnchor: 'middle' as const,
        isInside: true
      };
    }
    // Special case: If bar extends beyond left edge only
    else if (itemPositions.extendsLeft) {
      // Check if there's enough visible width for the label
      if (itemPositions.visibleWidth >= labelRequiredWidth) {
        // Center the label in the visible portion
        return {
          x: itemPositions.visibleStartX + (itemPositions.visibleWidth / 2),
          y: yPosition + 14,
          textAnchor: 'middle' as const,
          isInside: true
        };
      } else {
        // Not enough visible space, place it to the right of the visible portion
        return {
          x: itemPositions.visibleEndX + 10,
          y: yPosition + 14,
          textAnchor: 'start' as const,
          isInside: false
        };
      }
    }
    // Special case: If bar extends beyond right edge only
    else if (itemPositions.extendsRight) {
      // Check if there's enough visible width for the label
      if (itemPositions.visibleWidth >= labelRequiredWidth) {
        // Center the label in the visible portion
        return {
          x: itemPositions.visibleStartX + (itemPositions.visibleWidth / 2),
          y: yPosition + 14,
          textAnchor: 'middle' as const,
          isInside: true
        };
      } else {
        // Not enough visible space, place it to the left of the visible portion
        return {
          x: itemPositions.visibleStartX - 10,
          y: yPosition + 14,
          textAnchor: 'end' as const,
          isInside: false
        };
      }
    }
    else if (canLabelFitInside && labelFitsCompletely) {
      // For bars that are partially visible but label still fits completely
      return {
        x: itemPositions.visibleStartX + (itemPositions.visibleWidth / 2),
        y: yPosition + 14,
        textAnchor: 'middle' as const,
        isInside: true
      };
    } 
    else if (itemPositions.isOutOfBoundsRight) {
      // Item is off the right edge
      return {
        x: containerWidth - 10,
        y: yPosition + 14,
        textAnchor: 'end' as const,
        isInside: false
      };
    } 
    else if (itemPositions.isOutOfBoundsLeft) {
      // Item is off the left edge
      return {
        x: 10,
        y: yPosition + 14,
        textAnchor: 'start' as const,
        isInside: false
      };
    } 
    else {
      // For bars where label doesn't fit completely inside, place it outside
      // Determine which side has more space and place the label on that side
      const leftSpace = itemPositions.visibleStartX;
      const rightSpace = containerWidth - itemPositions.visibleEndX;
      
      if (rightSpace >= leftSpace && rightSpace >= labelWidth + 10) {
        // Position to the right of the bar with some padding
        return {
          x: itemPositions.visibleEndX + 10,
          y: yPosition + 14,
          textAnchor: 'start' as const,
          isInside: false
        };
      } else if (leftSpace >= labelWidth + 10) {
        // Position to the left of the bar with some padding
        return {
          x: itemPositions.visibleStartX - 10,
          y: yPosition + 14,
          textAnchor: 'end' as const,
          isInside: false
        };
      } else {
        // If there's not enough space on either side, try a middle position outside the bar
        // with adaptive anchor based on available space
        if (rightSpace > leftSpace) {
          // More space on right, so place there
          return {
            x: itemPositions.visibleEndX + 10,
            y: yPosition + 14,
            textAnchor: 'start' as const,
            isInside: false
          };
        } else {
          // More space on left, so place there
          return {
            x: itemPositions.visibleStartX - 10,
            y: yPosition + 14,
            textAnchor: 'end' as const,
            isInside: false
          };
        }
      }
    }
  }, [item.type, canLabelFitInside, itemPositions, yPosition, containerWidth, labelWidth]);
  
  // Event handlers
  const handleMouseEnter = useCallback((e: React.MouseEvent<SVGElement>) => {
    setIsMouseOver(true);
    
    // Calculate tooltip position
    const svgElement = e.currentTarget.ownerSVGElement;
    if (svgElement) {
      const rect = svgElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      onItemHover(item, {
        x,
        y,
        isNearRightEdge: rect.width - x < 200,
        isNearLeftEdge: x < 150,
        isNearTopEdge: y < 100
      });
    }
  }, [item, onItemHover]);
  
  const handleMouseLeave = useCallback(() => {
    setIsMouseOver(false);
    onItemHover(null);
  }, [onItemHover]);
  
  const handleClick = useCallback(() => {
    onItemClick(item);
  }, [item, onItemClick]);
  
  // Determine item appearance based on type
  const itemAppearance = useMemo(() => {
    const isWorkExperience = item.type === 'workExperience';
    const itemHeight = isWorkExperience ? 
      (isMouseOver || isHovered ? 28 : 24) : 
      16;
    
    return {
      height: itemHeight,
      width: isWorkExperience ? itemPositions.width : 16,
      x: isWorkExperience ? itemPositions.startX : xScale(item.startDate) - 8,
      y: isWorkExperience ? 
        yPosition - (isMouseOver || isHovered ? 2 : 0) : 
        yPosition - 8,
      rx: isWorkExperience ? 12 : 8,
      fill: TIMELINE_COLORS[item.type]
    };
  }, [
    item.type, 
    itemPositions.width, 
    itemPositions.startX, 
    xScale, 
    item.startDate, 
    yPosition, 
    isMouseOver, 
    isHovered
  ]);

  // Add this key for preventing overlapping labels by forcing re-renders when position changes
  const positionKey = itemPositions.positionHash || `${Math.round(itemPositions.startX)}-${Math.round(itemPositions.width)}`;

  // Filter out items that are completely out of view
  if (!itemPositions.isVisible) return null;
  
  // Render appropriate item based on type
  return (
    <>
      {/* Main item shape */}
      <Bar
        x={itemAppearance.x}
        y={itemAppearance.y}
        width={itemAppearance.width}
        height={itemAppearance.height}
        rx={itemAppearance.rx}
        fill={itemAppearance.fill}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{
          cursor: 'pointer',
          transition: 'height 0.2s ease, y 0.2s ease',
          filter: (isMouseOver || isHovered) && item.type === 'workExperience' ? 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))' : 'none'
        }}
      />
      
      {/* Hidden measurement text for work experiences */}
      {item.type === 'workExperience' && (
        <text
          ref={labelRef}
          x={0}
          y={0}
          opacity={0}
          fontSize={12}
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {`${item.position} at ${item.company}`}
        </text>
      )}
      
      {/* Visible label for work experiences */}
      {item.type === 'workExperience' && labelPosition && (
        <>
          {/* Optional background for non-inside labels */}
          {!labelPosition.isInside && (
            <rect
              key={`bg-${positionKey}`}
              x={labelPosition.textAnchor === 'start' ? labelPosition.x - 4 : labelPosition.x - labelWidth - 4}
              y={labelPosition.y - 10}
              width={labelWidth + 8}
              height={20}
              rx={4}
              fill="rgba(255, 255, 255, 0.85)"
              stroke="none"
              strokeWidth={0}
            />
          )}
          <text
            key={`text-${positionKey}`}
            x={labelPosition.x}
            y={labelPosition.y}
            fill={labelPosition.isInside ? "#FFFFFF" : "#000000"}
            fontSize={12}
            fontWeight="500"
            fontFamily="system-ui, -apple-system, sans-serif"
            textAnchor={labelPosition.textAnchor}
            dominantBaseline="middle"
            className="font-medium"
            style={{
              pointerEvents: 'none',
              transition: 'y 0.2s ease',
              textShadow: labelPosition.isInside ? "0px 1px 2px rgba(0,0,0,0.3)" : "none"
            }}
          >
            {`${item.position} at ${item.company}`}
          </text>
        </>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Re-render if these props change
  if (
    prevProps.item !== nextProps.item ||
    prevProps.isHovered !== nextProps.isHovered ||
    prevProps.yPosition !== nextProps.yPosition ||
    prevProps.containerWidth !== nextProps.containerWidth ||
    prevProps.xScale !== nextProps.xScale
  ) {
    return false; // Don't skip re-render
  }
  return true; // Skip re-render if above conditions aren't met
});

export default TimelineItem; 